import { Box, Divider, Heading, Icon, IconButton, InputRightElement, Link, Text } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { IconView, IconViewOff } from 'assets/icons';
import Button from 'components/Button';
import { FormikInput } from 'components/Formik';
import PasswordStrengthChecker from 'components/PasswordStrengthChecker';
import { routePaths } from 'global/routePaths';
import { useResetPassword } from 'hooks/auth/useResetPassword';
import { useParamsQuery } from 'hooks/common/useParamsQuery';

import { initialResetPasswordData, resetPasswordValidationSchema } from './schema';

const ResetPassword: React.FC<unknown> = () => {
  const { t } = useTranslation(['auth']);
  const passwordInputRef = useRef<any>(null);
  const searchQuery = useParamsQuery();

  const { isLoading, mutate } = useResetPassword();

  const handleFormSubmit = async (values: typeof initialResetPasswordData) => {
    const postData = {
      password: values.password,
      password_confirmation: values.confirmPassword,
      email: searchQuery.get('email'),
      token: searchQuery.get('token'),
    };
    mutate(postData);
  };

  const handleClickShowPassword = (
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
    showPassword: boolean,
    name: 'showPassword' | 'showConfirmPassword'
  ) => {
    setFieldValue(name, !showPassword);
  };

  return (
    <Box
      bg="white"
      display="flex"
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      minHeight="100vh"
      p={{
        base: '32px 16px',
        md: '60px',
        lg: '32px',
        xl: '60px',
        '2xl': '100px',
      }}
    >
      <Heading as="h2" size="3xl" mt={{ base: '8', md: '14' }} mb="3">
        {t('auth:resetYourPassword')}
      </Heading>
      <Text fontSize="md" fontWeight="semibold" color="gray.500" mb="9">
        {t('auth:youCanSetANewStrongPasswordHere')}.
      </Text>
      <Box maxWidth="500px" width="100%">
        <Formik
          initialValues={initialResetPasswordData}
          validationSchema={resetPasswordValidationSchema}
          onSubmit={handleFormSubmit}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <Box position="relative">
                <FormikInput
                  type={values.showPassword ? 'text' : 'password'}
                  name="password"
                  label={t('auth:newPassword')}
                  inputProps={{ ref: passwordInputRef, autoFocus: true }}
                  inputElement={
                    <InputRightElement width="4.5rem">
                      <IconButton
                        aria-label="toggle view password"
                        size="sm"
                        onClick={() => handleClickShowPassword(setFieldValue, values.showPassword, 'showPassword')}
                        icon={values.showPassword ? <Icon as={IconViewOff} /> : <Icon as={IconView} />}
                      ></IconButton>
                    </InputRightElement>
                  }
                />
                <PasswordStrengthChecker
                  passwordValue={values.password}
                  listProps={{
                    visibility: passwordInputRef?.current === document.activeElement ? 'visible' : 'hidden',
                    opacity: passwordInputRef?.current === document.activeElement ? 100 : 0,
                  }}
                />
              </Box>
              <FormikInput
                type={values.showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                label={t('auth:confirmPassword')}
                inputElement={
                  <InputRightElement width="4.5rem">
                    <IconButton
                      aria-label="toggle view password"
                      size="sm"
                      onClick={() =>
                        handleClickShowPassword(setFieldValue, values.showConfirmPassword, 'showConfirmPassword')
                      }
                      icon={values.showConfirmPassword ? <Icon as={IconViewOff} /> : <Icon as={IconView} />}
                    ></IconButton>
                  </InputRightElement>
                }
              />
              <Button type="submit" colorScheme="secondary" width="100%" size="lg" isLoading={isLoading}>
                {t('auth:setPassword')}
              </Button>
            </Form>
          )}
        </Formik>
        <Divider colorScheme="gray" borderStyle="dashed" mt="9" mb="6" />
        <Text fontSize="sm" color="gray.500">
          {t('auth:alreadyHaveTimeeroAccount')}
          <Link as={RouterLink} to={routePaths.auth.login} color="primary.200" ml="1">
            {t('auth:logIn')}
          </Link>
        </Text>
      </Box>
    </Box>
  );
};

export default ResetPassword;
